/**
 * SaveStatusIndicator - Shows save state with appropriate icon and styling.
 *
 * Displays different states: idle (optional content), saving (spinner),
 * saved (success), or error. Used in auto-save scenarios.
 */

import type { ReactNode } from 'react';
import { Loader2, Check, AlertCircle, FileEdit } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@lean-agility/design-system/react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'draft';

const indicatorVariants = cva('flex items-center justify-center', {
  variants: {
    size: {
      sm: 'size-3.5',
      md: 'size-4',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

export interface SaveStatusIndicatorProps extends VariantProps<typeof indicatorVariants> {
  /** Current save status */
  status: SaveStatus;
  /** Content to show when idle (e.g., row number) */
  idleContent?: ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SaveStatusIndicator - Visual feedback for auto-save state.
 *
 * @example
 * ```tsx
 * // Show row number when idle, spinner when saving
 * <SaveStatusIndicator status={saveStatus} idleContent={rowIndex + 1} />
 *
 * // Just show the icon without idle content
 * <SaveStatusIndicator status="saving" />
 * ```
 */
export function SaveStatusIndicator({ status, idleContent, size, className }: SaveStatusIndicatorProps) {
  const iconClass = indicatorVariants({ size });

  switch (status) {
    case 'draft':
      return (
        <span title="Draft - fill required fields to save" className={cn(iconClass, className)}>
          <FileEdit className={cn(indicatorVariants({ size }), 'text-amber-500')} />
        </span>
      );

    case 'saving':
      return (
        <span className={cn(iconClass, className)}>
          <Loader2 className={cn(indicatorVariants({ size }), `animate-spin text-text-subtle`)} />
        </span>
      );

    case 'saved':
      return (
        <span className={cn('text-feedback-success', className)}>
          {idleContent ?? <Check className={indicatorVariants({ size })} />}
        </span>
      );

    case 'error':
      return (
        <span className={cn('text-feedback-error', className)}>
          {idleContent ?? <AlertCircle className={indicatorVariants({ size })} />}
        </span>
      );

    case 'idle':
    default:
      return idleContent ? (
        <span
          className={cn(
            `
              text-text-muted
              dark:text-text-muted
            `,
            className,
          )}
        >
          {idleContent}
        </span>
      ) : null;
  }
}
